import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import accessibility from 'highcharts/modules/accessibility';
import { Alert } from '@c2fo/liquidity';
import { SyncIcon } from '@c2fo/liquidity/icons';
import { ChartLoadingSkeleton } from './ChartLoadingSkeleton';
import { buildChartOptions } from './utils/chartOptions';
import { useMarketPerformanceChart } from './hooks/useMarketPerformanceChart';
import { ChartControls } from './components/ChartControls';
import {
  getTimeFrameOptions,
  getGroupByOptions,
  getRecommendedGroupBy,
  TimeFrameOption,
  GroupByOption,
} from '../../services/marketPerformanceService';

accessibility(Highcharts);
HighchartsExporting(Highcharts);
HighchartsExportData(Highcharts);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
Highcharts.setOptions({ global: { useUTC: true } } as any);

export interface ChartStatsData {
  clearedEarn: number;
  clearedYield: number;
  clearedInvoiceAmount: number;
  clearedTakerCount: number;
  startDate: Date;
  endDate?: Date;
  label?: string;
}

interface MarketPerformanceChartProps {
  className?: string;
  currency?: string;
  marketUuid?: string;
  onStatsChange?: (stats: ChartStatsData) => void;
  onTimeFrameChange?: (label: string) => void;
  onError?: (error: Error) => void;
  selectedDateRange?: TimeFrameOption;
}

interface CustomDateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export const MarketPerformanceChart: React.FC<MarketPerformanceChartProps> = ({
  className = '',
  currency = 'USD',
  marketUuid,
  onStatsChange,
  onTimeFrameChange,
  onError,
  selectedDateRange: externalDateRange,
}) => {
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrameOption>(
    externalDateRange ?? getTimeFrameOptions()[0],
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByOption>(getGroupByOptions()[0]);
  const [customRange, setCustomRange] = useState<CustomDateRange>({ startDate: null, endDate: null });
  const [isCustomRangeSelected, setIsCustomRangeSelected] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const timeFrameOptions = getTimeFrameOptions();
  const groupByOptions = getGroupByOptions();

  const { data, loading, error, refetchData, updateDataForGroupBy } = useMarketPerformanceChart({
    selectedTimeFrame,
    selectedGroupBy,
    currency,
    marketUuid,
    isCustomRangeSelected,
    customRange,
    onStatsChange,
    onError,
  });

  useEffect(() => {
    if (externalDateRange) {
      setSelectedTimeFrame(externalDateRange);
      setIsCustomRangeSelected(false);
    }
  }, [externalDateRange]);

  const handleTimeFrameSelect = (timeFrame: TimeFrameOption) => {
    void onTimeFrameChange?.(timeFrame.label);
    setSelectedTimeFrame(timeFrame);
    setIsCustomRangeSelected(false);
    const recommendedGroupBy = getRecommendedGroupBy(timeFrame.startDate, timeFrame.endDate);
    if (selectedGroupBy.value === 'day' && recommendedGroupBy !== 'day') {
      const newGroupBy = groupByOptions.find((g) => g.value === recommendedGroupBy);
      if (newGroupBy) {
        setSelectedGroupBy(newGroupBy);
      }
    }
  };

  const handleGroupBySelect = (groupBy: GroupByOption) => {
    setSelectedGroupBy(groupBy);
    updateDataForGroupBy(groupBy);
  };

  const handleCustomRangeApply = () => {
    if (customRange.startDate && customRange.endDate && customRange.startDate <= customRange.endDate) {
      const start = customRange.startDate.toISOString().split('T')[0];
      const end = customRange.endDate.toISOString().split('T')[0];
      void onTimeFrameChange?.(`${start} - ${end}`);
      setIsCustomRangeSelected(true);
      setShowDatePicker(false);

      const recommendedGroupBy = getRecommendedGroupBy(customRange.startDate, customRange.endDate);
      if (selectedGroupBy.value === 'day' && recommendedGroupBy !== 'day') {
        const newGroupBy = groupByOptions.find((g) => g.value === recommendedGroupBy);
        if (newGroupBy) {
          setSelectedGroupBy(newGroupBy);
        }
      }
    }
  };

  const handleCustomRangeChange = (field: 'startDate' | 'endDate', value: Date | null) => {
    setCustomRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDateInput = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const chartOptions = buildChartOptions({
    data,
    selectedGroupBy,
    currency,
    marketUuid,
  });

  const handleRetry = () => {
    const label = isCustomRangeSelected
      ? customRange.startDate && customRange.endDate
        ? `${customRange.startDate.toISOString().split('T')[0]} - ${customRange.endDate.toISOString().split('T')[0]}`
        : 'Custom Range'
      : selectedTimeFrame.label;
    void onTimeFrameChange?.(label);
    refetchData();
  };

  if (loading) {
    return <ChartLoadingSkeleton className={className} />;
  }

  if (error) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Overall Market Performance</h2>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <Alert
            type="error"
            variant="standard"
            full
            title="Error loading market performance"
            description={error}
            action={{
              text: 'Retry',
              onClick: handleRetry,
            }}
          />
        </div>
      </div>
    );
  }

  function getCustomRangeLabel(): string {
    if (isCustomRangeSelected && customRange.startDate && customRange.endDate) {
      const start = customRange.startDate.toISOString().split('T')[0];
      const end = customRange.endDate.toISOString().split('T')[0];

      return `${start} - ${end}`;
    }

    return 'Custom Range';
  }

  return (
    <div className={className}>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Overall Market Performance</h2>
        <button
          type="button"
          onClick={handleRetry}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh data"
          aria-label="Refresh chart data"
        >
          <SyncIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 overflow-visible">
        <div className="flex overflow-visible relative">
          <div className="flex-1 min-w-0 overflow-visible">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
          </div>

          <ChartControls
            timeFrameOptions={timeFrameOptions}
            groupByOptions={groupByOptions}
            selectedTimeFrame={selectedTimeFrame}
            selectedGroupBy={selectedGroupBy}
            isCustomRangeSelected={isCustomRangeSelected}
            customRange={customRange}
            showDatePicker={showDatePicker}
            onTimeFrameSelect={handleTimeFrameSelect}
            onGroupBySelect={handleGroupBySelect}
            onCustomRangeToggle={() => setShowDatePicker(!showDatePicker)}
            onCustomRangeApply={handleCustomRangeApply}
            onCustomRangeCancel={() => setShowDatePicker(false)}
            onCustomRangeChange={handleCustomRangeChange}
            formatDateInput={formatDateInput}
            getCustomRangeLabel={getCustomRangeLabel}
          />
        </div>
      </div>
    </div>
  );
};
